import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Spinner, ToolTip } from 'components/Elements';
import { useCustomerDetails } from '../api/getCustomerDetails'; // keeping your original hook
import { useNavigate } from 'react-router-dom';
import { imageToBlob } from 'utils/ImageToBlob';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  FiActivity,
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiExternalLink,
  FiFlag,
  FiHash,
  FiInfo,
  FiPauseCircle,
  FiRefreshCw,
  FiShield,
  FiXCircle,
} from 'react-icons/fi';

const StatusBadge = ({ status }) => {
  const map = {
    active: {
      label: 'Active',
      cls: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30',
    },
    frozen: {
      label: 'Frozen',
      cls: 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/30',
    },
    scheduled_closure: {
      label: 'Scheduled Closure',
      cls: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30',
    },
    closed: {
      label: 'Closed',
      cls: 'bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/30',
    },
  };
  const m = map[status] || {
    label: status || 'Unknown',
    cls: 'bg-white/10 text-white/80 ring-1 ring-white/20',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${m.cls}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {m.label}
    </span>
  );
};

const Toggle = ({ checked, onChange, label, disabled }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-2 text-sm">
      <FiFlag className="opacity-70" />
      <span>{label}</span>
    </div>
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all ${
        checked ? 'bg-emerald-500/70' : 'bg-white/20'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110'}`}
      aria-pressed={checked}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

export const VaultDetailsDrawer = ({ isOpen, onClose, id }) => {
  // Using your original hook; assuming it returns a vault snapshot within.
  const { data, isLoading } = useCustomerDetails({ id });
  const navigate = useNavigate();

  // Adapt to flexible shapes (user, vault, etc.)
  const vault = useMemo(() => {
    // Try to locate a vault-like object in the response
    return (
      data?.vault ||
      data?.userDetails?.vault || // fallback if nested
      data?.userDetails ||
      {}
    );
  }, [data]);

  const user = data?.userDetails;
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  // Local state for service toggles (seed from metadata/flags if present)
  const initialFlags = useMemo(() => {
    const flags = vault?.serviceFlags || vault?.flags || {};
    return {
      deposit: !!flags.deposit,
      withdraw: !!flags.withdraw,
      swap: !!flags.swap,
      send: !!flags.send,
    };
  }, [vault]);

  const [flags, setFlags] = useState(initialFlags);
  useEffect(() => setFlags(initialFlags), [initialFlags]);

  // Buttons state
  const [busy, setBusy] = useState(false);

  // Hero image (if present on user)
  useEffect(() => {
    const fetchImage = async () => {
      if (user?.photo) {
        setImageLoading(true);
        const localURL = await imageToBlob(user.photo);
        if (localURL) setImageUrl(localURL);
        setImageLoading(false);
      } else {
        setImageUrl(null);
        setImageLoading(false);
      }
    };
    fetchImage();
  }, [user?.photo]);

  const status = vault?.status || 'active';
  const disabledByStatus = status === 'closed';

  const handleToggle = (key, value) => {
    setFlags((prev) => ({ ...prev, [key]: value }));
    // TODO: call your mutation here to persist per-vault service toggles
    // await mutateUpdateFlags({ vaultId: id, key, value })
  };

  const act = async (fn) => {
    try {
      setBusy(true);
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const onFreeze = () =>
    act(async () => {
      // await mutateFreezeVault({ id })
      // optimistic UI pattern can go here
    });

  const onUnfreeze = () =>
    act(async () => {
      // await mutateUnfreezeVault({ id })
    });

  const onScheduleClosure = () =>
    act(async () => {
      // await mutateScheduleClosure({ id, date: ... })
      // You can enhance with a date picker; keeping it simple for now
      alert('Closure scheduled (demo). Wire this to your mutation.');
    });

  const onCloseVault = () =>
    act(async () => {
      // await mutateCloseVault({ id })
      alert('Vault closed (demo). Wire this to your mutation.');
    });

  const viewMore = () => {
    navigate(`/vaults/${id}`);
    onClose?.();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title=""
      renderFooter={() => (
        <div className="w-full flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <StatusBadge status={status} />
            {vault?.scheduledClosureAt && (
              <span className="inline-flex items-center gap-1 text-xs opacity-80">
                <FiCalendar />
                Scheduled: {new Date(vault.scheduledClosureAt).toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onFreeze}
              disabled={busy || disabledByStatus || status === 'frozen'}
              className="px-3 py-2 rounded-lg bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <FiPauseCircle /> Freeze
            </button>
            <button
              onClick={onUnfreeze}
              disabled={busy || disabledByStatus || status !== 'frozen'}
              className="px-3 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <FiRefreshCw /> Unfreeze
            </button>
            <button
              onClick={onScheduleClosure}
              disabled={busy || status === 'closed'}
              className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <FiCalendar /> Schedule Closure
            </button>
            <ToolTip content="Irreversible action">
              <button
                onClick={onCloseVault}
                disabled={busy || status === 'closed'}
                className="px-3 py-2 rounded-lg bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <FiXCircle /> Close Vault
              </button>
            </ToolTip>
            <button
              onClick={viewMore}
              className="px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 inline-flex items-center gap-2"
            >
              <FiExternalLink /> View More
            </button>
          </div>
        </div>
      )}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5 ring-1 ring-white/10 flex items-center justify-center">
            {imageLoading ? (
              <div className="p-3">
                <Spinner size="sm" />
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt="User"
                className="w-full h-full object-cover"
              />
            ) : (
              <FiShield className="opacity-60 text-xl" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold leading-tight">
                {vault?.alias || vault?.name || 'Vault'}
              </h3>
              <StatusBadge status={status} />
            </div>
            <div className="text-sm opacity-80 flex flex-wrap items-center gap-3 mt-1">
              <span className="inline-flex items-center gap-1">
                <FiHash /> {vault?.id || id}
              </span>
              {vault?.currency && (
                <span className="inline-flex items-center gap-1">
                  <FiActivity /> {vault.currency}
                </span>
              )}
              {vault?.country && (
                <span className="inline-flex items-center gap-1">
                  <FiFlag /> {vault.country}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Balance Card */}
        <div className="rounded-2xl p-4 ring-1 ring-white/10 bg-gradient-to-br from-white/5 to-white/[0.03]">
          <div className="text-xs uppercase opacity-70">Balance</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">
            {isLoading ? (
              <Skeleton width={140} />
            ) : (
              <>
                {vault?.balance?.toLocaleString?.(undefined, {
                  maximumFractionDigits: 2,
                }) ?? '0.00'}{' '}
                <span className="text-sm opacity-70">
                  {vault?.currency || ''}
                </span>
              </>
            )}
          </div>
          {vault?.available != null && (
            <div className="mt-1 text-sm opacity-80">
              Available:{' '}
              {vault.available.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </div>
          )}
        </div>

        {/* Service Flags */}
        <div className="rounded-2xl p-4 ring-1 ring-white/10 bg-white/5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium flex items-center gap-2">
              <FiInfo className="opacity-70" /> Per-vault services
            </div>
            {status === 'frozen' && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                <FiAlertTriangle /> Some actions may be restricted
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Toggle
              label="Deposit"
              checked={flags.deposit}
              onChange={(v) => handleToggle('deposit', v)}
              disabled={disabledByStatus}
            />
            <Toggle
              label="Withdraw"
              checked={flags.withdraw}
              onChange={(v) => handleToggle('withdraw', v)}
              disabled={disabledByStatus}
            />
            <Toggle
              label="Swap"
              checked={flags.swap}
              onChange={(v) => handleToggle('swap', v)}
              disabled={disabledByStatus}
            />
            <Toggle
              label="Send"
              checked={flags.send}
              onChange={(v) => handleToggle('send', v)}
              disabled={disabledByStatus}
            />
          </div>
        </div>

        {/* Metadata */}
        <div className="rounded-2xl p-4 ring-1 ring-white/10 bg-white/5">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <FiHash className="opacity-70" /> Vault metadata
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {isLoading ? (
              <>
                <Skeleton height={18} />
                <Skeleton height={18} />
                <Skeleton height={18} />
                <Skeleton height={18} />
              </>
            ) : (
              <>
                <Meta label="Wire ID" value={vault?.wireId} />
                <Meta
                  label="Owner Email"
                  value={vault?.ownerEmail || user?.email}
                />
                <Meta label="Created" value={fmtDate(vault?.createdAt)} />
                <Meta label="Updated" value={fmtDate(vault?.updatedAt)} />
                <Meta label="Country" value={vault?.country} />
                <Meta label="Currency" value={vault?.currency} />
                <Meta label="Type" value={vault?.type} />
                <Meta label="Provider" value={vault?.provider} />
              </>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

const Meta = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="opacity-60">{label}</span>
    <span className="font-medium text-right truncate">{value ?? '-'}</span>
  </div>
);

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};
